import { CartItem, Product, ProductVariation, GtmItem } from '../types';
import type { OrderResult } from './orderService';

let _clientId: number | null | undefined;

function toGtmItem(product: Product, variation: ProductVariation, quantity?: number): GtmItem {
  return {
    item_id: product.id,
    item_name: product.name,
    item_category: product.categories[0]?.toString() ?? '',
    item_variant: variation.size && variation.size !== 'Standard'
      ? `${variation.size} / ${variation.color}`
      : variation.color || 'Default',
    price: variation.price,
    ...(quantity !== undefined && { quantity }),
  };
}

function cartItemToGtmItem(item: CartItem): GtmItem {
  return toGtmItem(item.product, item.variation, item.quantity);
}

const GTM_ID = 'GTM-KXGLRXBW';
const gtmEnabled = import.meta.env.VITE_GTM_ENABLED !== 'false';

function load(): void {
  if (!gtmEnabled) return;

  // Inject GTM script tag
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);

  // Inject noscript iframe
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.cssText = 'display:none;visibility:hidden';
  noscript.appendChild(iframe);
  document.body.prepend(noscript);
}

function push(event: Record<string, unknown>): void {
  if (!gtmEnabled) return;
  window.dataLayer = window.dataLayer ?? [];
  // Clear previous ecommerce object before each push per GA4 recommendation
  if ('ecommerce' in event) {
    window.dataLayer.push({ ecommerce: null });
  }
  window.dataLayer.push({
    ...(_clientId != null && { client_id: _clientId }),
    ...event,
  });
}

export const gtm = {
  load,

  init(clientId: number | null | undefined): void {
    _clientId = clientId;
  },

  pushClientId(clientId: number): void {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ client_id: clientId });
  },

  pushPageView(path: string): void {
    push({ event: 'page_view', page_path: path });
  },

  pushViewItem(product: Product, variation: ProductVariation): void {
    push({
      event: 'view_item',
      ecommerce: {
        currency: 'USD',
        value: variation.price,
        items: [toGtmItem(product, variation)],
      },
    });
  },

  pushAddToCart(product: Product, variation: ProductVariation, quantity: number): void {
    push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: variation.price * quantity,
        items: [toGtmItem(product, variation, quantity)],
      },
    });
  },

  pushRemoveFromCart(item: CartItem): void {
    push({
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'USD',
        value: item.variation.price * item.quantity,
        items: [cartItemToGtmItem(item)],
      },
    });
  },

  pushViewCart(cart: CartItem[], total: number): void {
    push({
      event: 'view_cart',
      ecommerce: {
        currency: 'USD',
        value: total,
        items: cart.map(cartItemToGtmItem),
      },
    });
  },

  pushBeginCheckout(cart: CartItem[], total: number): void {
    push({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'USD',
        value: total,
        items: cart.map(cartItemToGtmItem),
      },
    });
  },

  pushPurchase(masterOrderNo: string, orders: OrderResult[], cart: CartItem[] = []): void {
    const value = orders.reduce((sum, o) => sum + o.amount, 0);
    push({
      event: 'purchase',
      ecommerce: {
        transaction_id: masterOrderNo,
        currency: 'USD',
        value,
        ...(cart.length > 0 && { items: cart.map(cartItemToGtmItem) }),
      },
    });
  },
};
